import type { RunEventLevel } from '../types/events';

/**
 * Coordinator-level terminal event types. A coordinator-emitted member of
 * this set marks a run as finished (sub-agent terminal events with a
 * `parentRunId` do not).
 */
export const TERMINAL_EVENTS = new Set([
  'agent.run.completed',
  'agent.run.failed',
  'agent.run.cancelled',
]);

// ---------------------------------------------------------------------------
// Run-event detail tiers
//
// Subscriptions to `agent.run.*` can request a `level` (lifecycle ⊂ steps ⊂
// full). The tier of each subtype is the MINIMUM level at which it is
// delivered. This mirrors the server-owned, authoritative classification — keep
// it in sync with the backend WS contract. Subtypes below are the part AFTER
// `agent.run.`.
// ---------------------------------------------------------------------------

/** Delivered at every level (coarsest tier). */
const LIFECYCLE_SUBTYPES = [
  'started', 'completed', 'failed', 'cancelled',
  'delegated', 'delegate_completed', 'delegate_dismissed',
  'subagent_invoked', 'subagent_started', 'subagent_completed',
  'subagent_returned', 'subagents_all_returned',
  'agent_task_created', 'agent_task_started', 'agent_task_completed',
  'agent_task_failed', 'agent_task_blocked', 'agent_task_unblocked',
  'agent_task_deleted',
  'ask_user_requested', 'ask_user_answered', 'ask_user_cancelled',
  'ask_user_expired',
  'voice_session_started', 'voice_session_stopped',
] as const;

/** Adds to lifecycle at the `steps` tier. */
const STEPS_ONLY_SUBTYPES = [
  'skill_activated', 'skill_deactivated',
  'tool_call', 'tool_result',
  'reasoning_start', 'reasoning_stop',
  'response_start', 'response_stop',
  'final_answer_start', 'final_answer_stop',
  'context_compaction_start', 'context_compaction_stop',
  'canvas_created', 'surface_update', 'surface_deleted', 'surface_moved',
  'voice_transcript',
] as const;

/** Adds to steps at the `full` tier (high-frequency streaming).
 *  `voice_transcript_delta` is direct-socket only (never published to
 *  EventBridge) — classified defensively. */
const FULL_ONLY_SUBTYPES = [
  'reasoning_delta', 'response_delta', 'final_answer_delta',
  'voice_transcript_delta',
] as const;

const RUN_SUBTYPE_MIN_LEVEL = new Map<string, RunEventLevel>([
  ...LIFECYCLE_SUBTYPES.map((s) => [s, 'lifecycle'] as const),
  ...STEPS_ONLY_SUBTYPES.map((s) => [s, 'steps'] as const),
  ...FULL_ONLY_SUBTYPES.map((s) => [s, 'full'] as const),
]);

/**
 * Forward-compat: new subtypes in these families are lifecycle-tier even before
 * they're enumerated above, so a list/dashboard subscriber keeps receiving them.
 */
const LIFECYCLE_PREFIXES = ['subagent_', 'agent_task_', 'ask_user_', 'delegate'];

const LEVEL_RANK: Record<RunEventLevel, number> = {
  lifecycle: 0,
  steps: 1,
  full: 2,
};

/**
 * Minimum level at which an `agent.run.*` subtype (the part after
 * `agent.run.`) is delivered. Unknown subtypes default to `full` — so a newly
 * added, potentially high-frequency event never silently floods
 * lifecycle/steps subscribers until it's deliberately classified.
 */
export function runSubtypeMinLevel(subtype: string): RunEventLevel {
  const exact = RUN_SUBTYPE_MIN_LEVEL.get(subtype);
  if (exact) return exact;
  if (LIFECYCLE_PREFIXES.some((p) => subtype.startsWith(p))) return 'lifecycle';
  return 'full';
}

/**
 * Does a subscription requesting `requested` level receive an event of
 * `subtype`? True iff the event's minimum tier is ≤ the requested tier
 * (cumulative: full ⊇ steps ⊇ lifecycle).
 */
export function levelDelivers(requested: RunEventLevel, subtype: string): boolean {
  return LEVEL_RANK[runSubtypeMinLevel(subtype)] <= LEVEL_RANK[requested];
}
